import { useEffect, useState } from 'react';

/**
 * App-shell compact-layout breakpoint (px). At or below this width the core
 * screens reflow to a single column and the left navigation (primary rail +
 * any second-level panel such as chat history) collapses into an off-canvas
 * drawer toggled from the navbar. This covers phones AND portrait tablets
 * (e.g. iPad portrait ~768–1024), where the desktop three-column
 * rail + list + content layout is too cramped to be operable — notably the
 * chat-history master/detail. Kept in one place so the JS breakpoint and the
 * coupled CSS `@media (max-width: 1024px)` app-shell drawer block stay in
 * lock-step. (Per-screen content reflows may still use their own narrower
 * `@media (max-width: 768px)` phone tweaks — those are not drawer-coupled.)
 */
export const MOBILE_BREAKPOINT_PX = 1024;

const MOBILE_QUERY = `(max-width: ${MOBILE_BREAKPOINT_PX}px)`;

function matches(query: string): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(query).matches
  );
}

/**
 * Tracks whether the viewport is at or below the app-shell mobile breakpoint.
 *
 * SSR / no-`matchMedia` safe (returns `false`). Supports both the modern
 * `addEventListener('change')` API and the legacy `addListener` API used by the
 * jsdom/test `matchMedia` mock, mirroring `ThemeModeContext`'s matchMedia usage.
 */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => matches(MOBILE_QUERY));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(MOBILE_QUERY);
    const onChange = (event: MediaQueryListEvent) => setIsMobile(event.matches);

    // Re-sync in case the viewport changed between the initial render and the
    // effect firing (e.g. hydration or an orientation flip during mount).
    setIsMobile(mql.matches);

    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return isMobile;
}
